/**
 * Boss cinematic overlays — extracted from GameCanvas.jsx.
 *
 * Two sibling sub-trees that share a "darkness lifts to reveal /
 * darkness falls in triumph" cinematic vocabulary but with opposite
 * colour palettes:
 *
 *  - <BossEntranceCinematic> :: violet-toned, fades in over 2.4s to
 *    reveal the Leviathan already drawn on the canvas behind. Click/
 *    tap to skip; otherwise auto-clears at `durationMs`.
 *  - <BossDefeatedCinematic> :: green/gold companion to the entrance
 *    cinematic. 1.8s. Includes the S/A/B/C skill rank computed from
 *    lives remaining + kill time.
 *
 * Both are pure JSX with prop-only inputs. Animation keyframes
 * (`bossCinematicFade`, `bossDefeatedFade`, `bossRankPop` etc.) live
 * in `index.css` and are unaffected by the extraction.
 */
export function BossEntranceCinematic({ cinematic, onSkip, hidden = false }) {
  if (!cinematic || hidden) return null;
  const accent = cinematic.kind === "giant" ? "#ff8eff" : "#c95fff";
  const labelTier = cinematic.kind === "giant" ? "// APEX PREDATOR" : "// LEVIATHAN ENCOUNTER";
  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center cursor-pointer select-none"
      onClick={onSkip}
      data-testid="boss-cinematic"
      style={{
        background: "radial-gradient(circle at 50% 45%, rgba(5,5,12,0.45) 0%, rgba(5,5,12,0.92) 65%, rgba(0,0,0,0.96) 100%)",
        animation: "bossCinematicFade 2.4s ease-out forwards",
      }}
    >
      <div
        className="font-mono-cosmic text-[10px] sm:text-xs tracking-[0.5em] uppercase mb-3"
        style={{ color: accent, textShadow: `0 0 12px ${accent}` }}
        data-testid="boss-cinematic-tier"
      >
        {labelTier}
      </div>
      <div
        className="font-heading uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl text-white text-center px-4"
        style={{
          letterSpacing: "0.04em",
          textShadow: `0 0 24px ${accent}, 0 0 48px ${accent}80`,
          animation: "bossCinematicTitle 2.4s ease-out forwards",
        }}
        data-testid="boss-cinematic-label"
      >
        {cinematic.label}
      </div>
      {cinematic.levelName && (
        <div
          className="mt-3 font-mono-cosmic text-[11px] sm:text-sm tracking-[0.35em] uppercase"
          style={{ color: "#aef0ff", textShadow: "0 0 10px rgba(174,240,255,0.6)" }}
          data-testid="boss-cinematic-sector"
        >
          · {cinematic.levelName} ·
        </div>
      )}
      <div className="mt-8 font-mono-cosmic text-[9px] tracking-[0.4em] uppercase text-white/40">
        Tap / Space to skip
      </div>
    </div>
  );
}

export function BossDefeatedCinematic({ cinematic, hidden = false }) {
  if (!cinematic || hidden) return null;
  const accent = cinematic.kind === "giant" ? "#ffd700" : "#7cffb0";
  const tier = cinematic.kind === "giant" ? "// APEX KILL" : "// VICTORY";
  const rank = cinematic.rank || "C";
  const rankColor =
    rank === "S" ? "#ffd700"
      : rank === "A" ? "#7cffb0"
      : rank === "B" ? "#67e8f9"
      : "#cbd5e1";
  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center cursor-pointer select-none pointer-events-none"
      data-testid="boss-defeated-cinematic"
      style={{
        background: "radial-gradient(circle at 50% 45%, rgba(5,12,8,0.45) 0%, rgba(2,8,4,0.85) 65%, rgba(0,0,0,0.92) 100%)",
        animation: "bossDefeatedFade 1.8s ease-out forwards",
      }}
    >
      <div
        className="font-mono-cosmic text-[10px] sm:text-xs tracking-[0.5em] uppercase mb-3"
        style={{ color: accent, textShadow: `0 0 12px ${accent}` }}
        data-testid="boss-defeated-tier"
      >
        {tier}
      </div>
      <div
        className="font-heading uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl text-white text-center px-4"
        style={{
          letterSpacing: "0.04em",
          textShadow: `0 0 24px ${accent}, 0 0 48px ${accent}80`,
          animation: "bossDefeatedTitle 1.8s ease-out forwards",
        }}
        data-testid="boss-defeated-label"
      >
        {cinematic.label}
      </div>
      <div className="mt-6 flex items-center gap-5">
        <div
          className="font-heading text-7xl sm:text-8xl"
          style={{
            color: rankColor,
            textShadow: `0 0 28px ${rankColor}, 0 0 56px ${rankColor}80`,
            animation: "bossRankPop 1.8s ease-out forwards",
          }}
          data-testid="boss-defeated-rank"
        >
          {rank}
        </div>
        <div className="flex flex-col items-start gap-1">
          <div className="font-mono-cosmic text-[9px] tracking-[0.4em] uppercase text-white/50">
            RANK
          </div>
          <div
            className="font-mono-cosmic text-xs tracking-[0.3em] uppercase tabular-nums"
            style={{ color: rankColor }}
            data-testid="boss-defeated-time"
          >
            {cinematic.elapsedS}s
          </div>
        </div>
      </div>
    </div>
  );
}
